<template>
  <v-container class="living-will-container standard-content-container">
    <DynamicNavDrawer
      :drawerOpen="readMoreDrawerOpen"
      @drawerClosed="drawerClosed"
      :title="'Importance of a living will'"
      :description="'Considerations when creating your will'"
    >
      <template v-slot:content>
        <Accordion
          :accordionData="livingWillData"
          :html="true"
          :isNavDrawer="true"
        />
      </template>
    </DynamicNavDrawer>
    <v-row>
      <v-col cols="12" md="5">
        <v-row no-gutters class="title-section">
          <v-col
            cols="12"
            sm="2"
            class="link-container"
            :class="{
              'small-container':
                this.$vuetify.breakpoint.width <=
                this.$vuetify.breakpoint.thresholds.xs,
            }"
          >
            <router-link :to="{ name: 'LetterOfWishes' }" class="back-arrow">
              <v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon>
            </router-link>
          </v-col>
          <v-col cols="12" sm="10" class="standard-title-container">
            <h1>Living Will</h1>
            <template v-if="$vuetify.breakpoint.mdAndUp">
              <AdditionalInfoComponent
                v-if="livingWillData"
                :heading="'Importance of a living will'"
                :source="livingWillVideo"
                @drawerOpen="drawerOpen()"
              ></AdditionalInfoComponent>
            </template>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="7">
        <div class="left-section-container">
          <ValidationObserver ref="observer">
            <v-form class="mt-6" @submit.prevent="submit">
              <p class="living-will-label">
                When you can no longer make decisions for yourself, and your
                Living Will does not make provision for the situation, who do
                you appoint to make decisions on your behalf?*
              </p>
              <PeopleList
                :people="livingWillDecisionMakerArray"
                :testator="testator"
                relation="LIVING_WILL_DECISION_MAKER"
                :people-for-dialog="peopleForLivingWillDialog"
                :limit="1"
                :decisionMaker="decisionMaker"
                :peopleDialogShow="peopleDialogShow"
                @decisionMakerRemoved="updateDataLayer(true)"
              />
              <ValidationProvider
                ref="decisionMakerInput"
                id="living_will_decision_maker"
                v-slot="{ errors }"
                name="living_will_decision_maker"
                :rules="{
                  required: true,
                  emailNotNull: {
                    email: peopleRelations?.livingWillDecisionMaker?.email,
                    decisionMakerNotify: livingWill.decisionMakerNotify,
                  },
                }"
              >
                <input
                  :value="peopleRelations.livingWillDecisionMaker.id"
                  type="hidden"
                />
                <span class="error-text">{{ errors[0] }}</span>
              </ValidationProvider>
              <ValidationProvider
                id="living_will_decision_maker_age"
                v-slot="{ errors }"
                name="living_will_decision_maker_age"
                :rules="decisionMakerAgeRules"
              >
                <input
                  :value="peopleRelations?.livingWillDecisionMaker?.dateOfBirth"
                  type="hidden"
                />
                <span class="error-text">{{ errors[0] }}</span>
              </ValidationProvider>
              <ValidationProvider
                id="decision_maker_notify"
                v-slot="{ errors }"
                name="decision_maker_notify"
                rules="required"
              >
                <p class="living-will-label">
                  Do you want us to inform the person above that they have been
                  appointed to manage your living will and to make decisions on
                  your behalf?
                </p>
                <RadioSelect
                  v-model="livingWill.decisionMakerNotify"
                  :items="decisionMakerNotifyOptions"
                  :group="'decisionMakerNotify'"
                  :errors="errors"
                  @change="handleDecisionMakerNotify"
                />
              </ValidationProvider>
              <p class="living-will-label living-will-disclaimer">
                A Living Will is not legally binding. It reflects your
                preferences for medical care, which may guide, but cannot
                compel, your healthcare team's decisions.
              </p>

              <ValidationProvider
                id="kept_alive_by_artificial_means"
                v-slot="{ errors }"
                name="kept_alive_by_artificial_means"
                rules="required"
              >
                <p class="living-will-label">
                  Are you willing to be kept alive by artificial means, for any
                  amount of time?*
                </p>
                <RadioSelect
                  v-model="livingWill.keptAliveByArtificialMeans"
                  :items="keptAliveByArtificialMeansOptions"
                  :group="'kept_alive_by_artificial_means'"
                  :errors="errors"
                />
              </ValidationProvider>

              <div v-if="livingWill.keptAliveByArtificialMeans">
                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <p class="living-will-label">For how long?</p>
                  </v-col>
                  <v-col cols="12" sm="8" class="kept-alive-time-container">
                    <ValidationProvider
                      id="kept_alive_time_amount"
                      v-slot="{ errors }"
                      name="kept_alive_time_amount"
                      rules="required"
                    >
                      <ListSelect
                        v-model="livingWill.keptAliveTimeAmount"
                        :items="$options.keptAliveTimeAmounts"
                        :name="'kept_alive_time_amount'"
                        :label="'Kept alive time amount'"
                        :outlined="true"
                        :errors="errors"
                      />
                    </ValidationProvider>

                    <ValidationProvider
                      id="kept_alive_time_period"
                      v-slot="{ errors }"
                      name="kept_alive_time_period"
                      rules="required"
                    >
                      <ListSelect
                        v-model="livingWill.keptAliveTimePeriod"
                        :items="$options.keptAliveTimePeriods"
                        :name="'kept_alive_time_period'"
                        :label="'Kept alive time period'"
                        :outlined="true"
                        :errors="errors"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </div>

              <ValidationProvider
                id="resuscitate"
                v-slot="{ errors }"
                name="resuscitate"
                rules="required"
              >
                <p class="living-will-label">
                  If there is no reasonable prospect of your recovery from
                  physical illness or if your impairment is expected to cause
                  you severe distress or to render you incapable of rational
                  existence, do you still wish to be resuscitated, whether it be
                  manually or by electric shock?*
                </p>
                <RadioSelect
                  v-model="livingWill.resuscitate"
                  :items="resuscitateOptions"
                  :group="'keptAliveByArtificialMeans'"
                  :errors="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                id="comfort_care"
                v-slot="{ errors }"
                name="comfort_care"
                rules="required"
              >
                <p class="living-will-label">
                  Do you consent to the use of drugs to keep you free from pain
                  or distress even if the moment of death is hastened?*
                </p>
                <RadioSelect
                  v-model="livingWill.comfortCare"
                  :items="comfortCareOptions"
                  :group="'comfortCare'"
                  :errors="errors"
                />
              </ValidationProvider>

              <div class="text-left mt-4 submit-btn-container">
                <GradientButton
                  :title="'Next'"
                  :small="true"
                  :callout="'right'"
                  :loading="buttonLoading"
                  type="submit"
                />
              </div>
            </v-form>
          </ValidationObserver>
          <template v-if="$vuetify.breakpoint.smAndDown">
            <AdditionalInfoComponent
              v-if="livingWillData"
              :heading="'Importance of a living will'"
              :source="livingWillVideo"
              @drawerOpen="drawerOpen()"
            ></AdditionalInfoComponent>
          </template>
        </div>
      </v-col>
    </v-row>
    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import PeopleList from '../people/PeopleList';
  import { getAge } from '../../constants/js/people.js';
  import { CREATE_OR_UPDATE_LIVING_WILL_MUTATION } from '../../constants/graphql/living-wills';
  import DynamicNavDrawer from '../guest/DynamicNavDrawer.vue';
  import Accordion from '../common/Accordion.vue';
  import AdditionalInfoComponent from '../people/AdditionalInfoComponent.vue';

  export default {
    name: 'LivingWill',
    components: {
      ErrorSnackbar,
      PeopleList,
      DynamicNavDrawer,
      Accordion,
      AdditionalInfoComponent,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
      peopleRelations: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        livingWill: {},
        loading: true,
        livingWillVideo: null,
        livingWillData: [],
        readMoreDrawerOpen: false,
        decisionMaker: true,
        peopleDialogShow: false,
        decisionMakerId: null,
        livingWillClone: null,
        decisionMakerNotifyOptions: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
        keptAliveByArtificialMeansOptions: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
        resuscitateOptions: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
        comfortCareOptions: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
      };
    },
    computed: {
      isMobile() {
        return this.$isMobileDevice();
      },
      livingWillDecisionMakerArray() {
        return this.peopleRelations.livingWillDecisionMaker.id
          ? [this.peopleRelations.livingWillDecisionMaker]
          : [];
      },
      peopleForLivingWillDialog() {
        return this.testator.people.map(p => {
          if (this.livingWillDecisionMakerArray.includes(p)) {
            return {
              ...p,
              disabledMessage: 'This person is already the decision maker',
            };
          } else if (p.dateOfBirth && getAge(p.dateOfBirth) < 18) {
            return {
              ...p,
              disabledMessage: 'This person is younger than 18 years old',
            };
          } else {
            return p;
          }
        });
      },
      decisionMakerAgeRules() {
        const dm = this.peopleRelations?.livingWillDecisionMaker;
        if (!dm?.id) return {};
        if (dm.isAdult) return {};
        if (dm.dateOfBirth) return { minimum_age: { age: 18 } };
        return {};
      },
    },
    keptAliveTimeAmounts: [...Array(30).keys()].map(i => ({
      text: (i + 1).toString(),
      value: i + 1,
    })),
    keptAliveTimePeriods: [
      { text: 'Day(s)', value: 'DAYS' },
      { text: 'Week(s)', value: 'WEEKS' },
      { text: 'Month(s)', value: 'MONTHS' },
      { text: 'Year(s)', value: 'YEARS' },
    ],
    mounted() {
      this.livingWillVideo = this.whitelabel.videos.livingWillVideo;
      this.livingWillData = this.whitelabel.livingWillData;
    },
    created() {
      this.livingWill = this.testator.livingWill
        ? JSON.parse(JSON.stringify(this.testator.livingWill))
        : {};
      this.decisionMakerId = this.peopleRelations.livingWillDecisionMaker.id;
      this.livingWillClone = { ...this.livingWill };

      const queriedBurialWishes = this.$route.query.includeBurialWishes;
      if (queriedBurialWishes !== undefined) {
        this.livingWill.includeBurialWishes =
          { true: true, false: false }[queriedBurialWishes] ?? null;
      }
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.createOrUpdateLivingWill();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createOrUpdateLivingWill() {
        const livingWillInputDetails = {
          testatorId: this.testatorId,
          keptAliveByArtificialMeans:
            this.livingWill.keptAliveByArtificialMeans,
          keptAliveTimePeriod: this.livingWill.keptAliveTimePeriod,
          keptAliveTimeAmount: parseInt(this.livingWill.keptAliveTimeAmount),
          resuscitate: this.livingWill.resuscitate,
          comfortCare: this.livingWill.comfortCare,
          decisionMakerNotify: this.livingWill.decisionMakerNotify,
          includeBurialWishes: this.livingWill.includeBurialWishes,
        };

        this.$apollo
          .mutate({
            mutation: CREATE_OR_UPDATE_LIVING_WILL_MUTATION,
            variables: {
              livingWillInputDetails,
            },
          })
          .then(_response => {
            this.determineLivingWillChanges();
            this.$router.push({
              name: 'OverviewPage',
              hash: '#letter-of-wishes',
            });
            this.buttonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      drawerOpen() {
        this.readMoreDrawerOpen = true;
      },
      drawerClosed() {
        this.readMoreDrawerOpen = false;
      },
      handleDecisionMakerNotify(value) {
        if (value !== true) {
          this.peopleDialogShow = false;
          return;
        }

        if (!this.peopleRelations?.livingWillDecisionMaker?.id) {
          this.peopleDialogShow = true;
        } else if (
          this.peopleRelations?.livingWillDecisionMaker?.id &&
          !this.peopleRelations?.livingWillDecisionMaker?.email
        ) {
          this.$nextTick(() => this.$refs.decisionMakerInput.validate());
        }
      },
      determineLivingWillChanges() {
        if (
          JSON.stringify(this.livingWillClone) !==
          JSON.stringify(this.livingWill)
        ) {
        } else if (
          this.peopleRelations?.livingWillDecisionMaker?.id &&
          this.peopleRelations?.livingWillDecisionMaker?.id !==
            this.decisionMakerId
        ) {
          this.updateDataLayer();
        }
      },
      updateDataLayer(remove = false) {
        if (remove) {
          this.decisionMakerId = null;
        }
        if (this.livingWill.decisionMakerNotify) {
          dataLayer.push({
            event: 'living_will_person_notified',
            omuserid: this.$store.getters?.userId,
            omtid: this.$store.getters?.omtId,
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .living-will-container {
    padding-top: 40px;

    .read-more-column {
      border-top: 1px solid #d9d9d9;
      border-bottom: 1px solid #d9d9d9;
      padding: 30px 0;
      h2 {
        font-size: 18px;
        font-weight: 600;
        line-height: 23.4px;
        color: var(--v-primary-base);
      }

      .video-container {
        margin: 26px 0;
        video {
          width: 100%;
          max-width: 100%;
        }
      }

      .read-more {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        width: 123px;
        height: 36px;
        border-radius: 100px;
        border: 1px solid #dcddde;
        color: #282828;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        letter-spacing: 0.4px;
        text-align: center;
        cursor: pointer;
        span {
          padding: 8px 16px;
        }
      }
    }
    .header-row {
      padding-bottom: 40px;
      display: flex;
      gap: 76px;
      h1 {
        flex: 1;
      }
      .your-people-description {
        max-width: 331px;
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        text-align: left;
        color: #282828;
        padding-top: 20px;
        max-width: 331px;
      }
    }

    .living-will-label {
      margin-top: 20px;
      margin-bottom: 0;
    }
    .submit-btn-container {
      margin-bottom: 44px;
    }
    .kept-alive-time-container {
      padding-top: 18px;
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }

    .living-will-disclaimer {
      color: black;
      font-weight: 700 !important;
      font-size: 16px;
      line-height: 24px;
    }
  }
</style>
